import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  AlarmClock,
  CloudDownload,
  EllipsisVertical,
  ExternalLink,
  Hand,
  History,
  Mail,
  ThumbsUp,
} from 'lucide-react';
import { toast } from 'sonner';
import cn from 'classnames';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../redux/features/user/userSelectors';
import { useRequestLikes } from '../../../../hooks/useRequestLikes';
import { useAddRealtyLikeMutation } from '../../../../redux/services/realties/realtiesApi';
import {
  useGetEventLogsBySubjectQuery,
  EVENT_LOG_SUBJECTS,
} from '../../../../redux/services/eventLog/eventLogApi';

import { Dialog } from '../../Dialog';
import { MatchingComponent } from '../../matchingComponents/MatchingComponent';
import { AddReminderForm } from '../../../form/forms/AddReminderForm';
import { DropdownDownload } from '../DropdownDownload';
import { flattenPhotos } from '../CardRealtyPhotos';
import { HistoryDialog } from '../HistoryDialog';

import { errorMessages, matchingValues } from '../../../../data/constants';
import { handleLikeChange, loadLikeData } from '../../../../utils/ui';

const BUBBLE_STEP = 38;

// 36/32px bubbles; the ::before ring stretches the hit target to ~44px on touch
const bubbleClasses =
  'group relative flex flex-none items-center justify-center rounded-full bg-white/70 shadow-[0_1px_4px_rgba(0,0,0,0.18)] hover:bg-white/90 before:absolute before:-inset-1.5 before:content-[""]';

const iconClasses = 'text-[#30353c] group-hover:text-[#3e4590]';

// No crossed-out hand in lucide: draw the slash inside the Hand svg
const HandOff = props => (
  <Hand {...props}>
    <line
      x1="2"
      y1="2"
      x2="22"
      y2="22"
    />
  </Hand>
);

export const CardQuickActions = ({
  realty,
  canEdit = false,
  canClaim = false,
  onClaim = () => {},
  canRelease = false,
  onRelease = () => {},
  isOpen,
  setIsOpen,
  onLikeLoadingChange,
}) => {
  const { id, likes, main } = realty;
  const externalLink = main?.external_link;
  const hasPhotos = flattenPhotos(realty.photo?.photos).length > 0;

  const navigate = useNavigate();
  const rootRef = useRef(null);
  const { searchRequest: request } = useRequestLikes();
  const userToken = useSelector(selectUserToken);
  const [addLike, { isLoading: isAddingLike }] = useAddRealtyLikeMutation();

  const [likeStatus, setLikeStatus] = useState(matchingValues[0]);
  const [isMatchingOpen, setIsMatchingOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isReminderOpen, setIsReminderOpen] = useState(false);

  const { data: logs = [] } = useGetEventLogsBySubjectQuery(
    { token: userToken, subjectType: EVENT_LOG_SUBJECTS.REALTY, subjectId: id },
    { skip: !userToken || !id }
  );

  const handleMailClick = () => {
    navigate('/emails', {
      state: {
        realty,
        clientId: request?.client_id ?? null,
        requestId: request?.id ?? null,
      },
    });
  };

  const actions = [
    canClaim && { label: 'Claim', icon: Hand, run: onClaim },
    canRelease && { label: 'Return to pool', icon: HandOff, run: onRelease },
    { label: 'Send by email', icon: Mail, run: handleMailClick },
    logs.length > 0 && {
      label: 'History',
      icon: History,
      run: () => setIsHistoryOpen(true),
    },
    canEdit && {
      label: 'Set reminder',
      icon: AlarmClock,
      run: () => setIsReminderOpen(true),
    },
    externalLink && {
      label: 'Open source ad',
      icon: ExternalLink,
      run: () => window.open(externalLink),
    },
  ].filter(Boolean);

  const handleLikeClick = async () => {
    if (!request) {
      setIsMatchingOpen(true);
      return;
    }

    const previousLikeStatus = likeStatus;
    const nextLikeType = handleLikeChange(likeStatus, setLikeStatus);

    onLikeLoadingChange?.(true);

    const response = await addLike({
      token: userToken,
      data: {
        realty_id: id,
        client_request_id: request.id,
        like_type: nextLikeType,
      },
    });

    onLikeLoadingChange?.(false);

    if (response?.error) {
      toast.error(errorMessages.wentWrong);
      setLikeStatus(previousLikeStatus);
    }
  };

  // Card clicks must not reach the card/listing underneath
  const stop = handler => event => {
    event.stopPropagation();
    handler();
  };

  useEffect(() => {
    if (likes && request) {
      loadLikeData(likes, request.id, setLikeStatus);
    }
  }, [likes?.like?.length, likes?.dislike?.length]);

  // Esc or a click anywhere else (incl. another card's trigger) closes the menu
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = event => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    const handlePointerDown = event => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isOpen, setIsOpen]);

  const LikeIcon = request ? likeStatus.icon : ThumbsUp;

  return (
    <>
      {isOpen && (
        <div
          className="absolute inset-0 z-[2] bg-[rgba(20,22,28,0.28)]"
          onClick={stop(() => setIsOpen(false))}
        />
      )}

      <div
        ref={rootRef}
        className="absolute right-2.5 top-2.5 z-[3] flex flex-col items-end gap-1.5"
      >
        <button
          type="button"
          aria-label="Quick actions"
          aria-expanded={isOpen}
          onClick={stop(() => setIsOpen(!isOpen))}
          className={cn(bubbleClasses, 'w-9 h-9', {
            '!bg-[#30353c]': isOpen,
          })}
        >
          <EllipsisVertical
            size={18}
            className={cn(
              'transition-transform duration-200',
              isOpen ? 'rotate-90 text-white' : iconClasses
            )}
          />
        </button>

        {actions.map(({ label, icon: Icon, run }, index) => {
          const delay = isOpen
            ? index * 30
            : (actions.length - 1 - index) * 20;
          const hiddenTransform = `translateY(${-(index + 1) * BUBBLE_STEP}px) scale(0.6)`;

          return (
            <button
              key={label}
              type="button"
              aria-label={label}
              tabIndex={isOpen ? 0 : -1}
              onClick={stop(() => {
                setIsOpen(false);
                run();
              })}
              className={cn(
                'group flex items-center gap-2 transition-[opacity,transform] duration-[180ms] ease-in-out will-change-[opacity,transform]',
                { 'pointer-events-none': !isOpen }
              )}
              style={{
                opacity: isOpen ? 1 : 0,
                transform: isOpen ? 'none' : hiddenTransform,
                transitionDelay: `${delay}ms`,
              }}
            >
              <span className="px-[9px] py-[3px] rounded-[10px] bg-[rgba(20,22,28,0.72)] text-xs text-white whitespace-nowrap">
                {label}
              </span>
              <span className={cn(bubbleClasses, 'w-8 h-8')}>
                <Icon
                  size={17}
                  className={iconClasses}
                />
              </span>
            </button>
          );
        })}
      </div>

      <div className="absolute left-2.5 bottom-2.5 z-[1] flex gap-2">
        <button
          type="button"
          aria-label="Like"
          disabled={isAddingLike}
          onClick={stop(handleLikeClick)}
          className={cn(bubbleClasses, 'w-9 h-9', {
            'opacity-60': isAddingLike,
          })}
        >
          <LikeIcon
            size={17}
            className={request ? undefined : iconClasses}
            color={request ? likeStatus.color : undefined}
          />
        </button>

        <button
          type="button"
          aria-label="Download media"
          onClick={stop(() => setIsDownloadOpen(open => !open))}
          className={cn(bubbleClasses, 'w-9 h-9')}
        >
          <CloudDownload
            size={17}
            className={iconClasses}
          />
        </button>
      </div>

      {isDownloadOpen && (
        <div className="absolute left-0 top-0 z-[3]">
          <DropdownDownload
            realtyId={id}
            hasPhotos={hasPhotos}
            setIsOpen={setIsDownloadOpen}
          />
        </div>
      )}

      {isMatchingOpen && (
        <Dialog
          content={
            <MatchingComponent
              realtyId={id}
              handleCancel={() => setIsMatchingOpen(false)}
            />
          }
          classes={'w-11/12 h-[500px] sm:w-[768px] sm:h-[700px]'}
          isOpen={isMatchingOpen}
          onClose={() => setIsMatchingOpen(false)}
        />
      )}

      {isHistoryOpen && (
        <Dialog
          content={<HistoryDialog realtyId={id} />}
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
        />
      )}

      {isReminderOpen && (
        <Dialog
          content={
            <AddReminderForm
              realtyId={id}
              setIsAddOpen={setIsReminderOpen}
            />
          }
          isOpen={isReminderOpen}
          onClose={() => setIsReminderOpen(false)}
          classes="max-w-[290px] sm:max-w-[420px]"
        />
      )}
    </>
  );
};
